import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  ViewRef,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { DialogOptions } from 'src/app/shared/models/dto';
import { ContainerService } from 'src/app/shared/services/container.service';

import { BaseComponent } from '../base-component/base-component';
import { ImageSliderGalleryComponent } from './image-slider-gallery/image-slider-gallery.component';

@Component({
  selector: 'app-image-slider',
  templateUrl: './image-slider.component.html',
  styleUrls: ['./image-slider.component.scss'],
  animations: [
    trigger('fade', [
      state('opaque', style({
        opacity: 1
      })),
      state('transparent', style({
        opacity: 0
      })),
      transition('opaque => transparent', [
        animate('700ms ease-out')
      ]),
      transition('transparent => opaque', [
        animate('700ms ease-in')
      ])
    ])
  ]
})
export class ImageSliderComponent extends BaseComponent implements OnInit, OnDestroy {

  @Input() images: string[];
  @Input() descriptions: string[];
  @Input() maximumHeight: number;
  @Input() minimumHeight: number;
  @Input() height: number;
  @Input() maximumWidth: number;
  @Input() minimumWidth: number;
  @Input() showButtons: boolean = true;
  @Input() autoSlide: boolean = true;
  @Input() fullHeight: boolean = false;
  @Input() updateSize: Subject<void>;
  @Input() intervalStart: Subject<void>;
  @Input() intervalStop: Subject<void>;
  @Input() showImage: Subject<number>;
  @Input() imageChangeRate: number = 7000;
  @Input() equalDimension: boolean = true;
  @Input() fullScreenImage: boolean = true;
  @Input() startIndex: number = 0;
  @Input() gallery: boolean = true;
  @Input() galleryBottom: boolean = false;

  @Output() imagesLoaded: EventEmitter<any> = new EventEmitter();
  @Output() onButtonsClick: EventEmitter<any> = new EventEmitter();
  @Output() onImageChangeStart: EventEmitter<number> = new EventEmitter();
  @Output() onImageChangeStop: EventEmitter<any> = new EventEmitter();

  @ViewChildren('img') imgs: QueryList<ElementRef>;

  public imageIndex: number;
  public minHeight: number = 0;
  public minWidth: number = 0;
  public sub: Subscription;
  public sub1: Subscription;
  public sub2: Subscription;
  public sub3: Subscription;
  public sub4: Subscription;
  public interval: NodeJS.Timeout;
  public loadedImages: number = 0;
  public statuses: ('opaque' | 'transparent')[] = [];
  public disableButtons: boolean = false;
  public initialized: boolean = false;
  public imagesNotLoaded: number[] = [];
  public hover: boolean = false;

  public readonly DEFAULT_IMAGE = 'assets/images/sbm/no-image-big.png';

  constructor(private containerService: ContainerService,
    private cdRef: ChangeDetectorRef) {
    super();
  }

  ngOnInit() {
    this.imageIndex = this.startIndex;

    if (this.updateSize != null && this.equalDimension) {
      this.sub1 = this.updateSize.subscribe(() => {
        this.refreshSize();
      });
    }

    this.sub = this.containerService.windowResizeEvent.subscribe(() => {
      if (this.equalDimension) {
        this.refreshSize();
      }
    });

    if (this.intervalStart != null) {
      this.sub2 = this.intervalStart.subscribe(() => {
        if (this.initialized) {
          this.resumeInterval(this.autoSlide);
        }
      });
    }

    if (this.intervalStop != null) {
      this.sub3 = this.intervalStop.subscribe(() => {
        if (this.initialized) {
          this.stopInterval(this.autoSlide);
        }
      });
    }

    if (this.showImage != null) {
      this.sub4 = this.showImage.subscribe((index: number) => {
        if (this.initialized) {
          this.viewImage(index);
        }
      });
    }
  }

  refreshSize() {
    if (this.initialized) {
      if (!this.fullHeight) {
        this.minHeight = 0;

        setTimeout(() => {
          this.calculateMinHeight();
        }, 200);
      } else {
        this.minWidth = 0;

        setTimeout(() => {
          this.calculateMinWidth();
        }, 200);
      }
    }
  }

  calculateMinHeight() {
    let min = Number.POSITIVE_INFINITY;

    for (let img of this.imgs.toArray()) {
      if (!!img.nativeElement.offsetHeight) {
        min = Math.min(min, img.nativeElement.offsetHeight);
      }
    }

    if (min == Number.POSITIVE_INFINITY) {
      min = 0;
    }

    if (!!this.height || !!this.minimumHeight || !!this.maximumHeight) {
      if (!!this.height) {
        this.minHeight = this.height;
      } else {
        if (!!this.minimumHeight && !!this.maximumHeight) {
          this.minHeight = Math.max(this.minimumHeight, Math.min(min, this.maximumHeight));
        } else {
          if (!!this.maximumHeight) {
            this.minHeight = Math.min(min, this.maximumHeight);
          }
          if (!!this.minimumHeight) {
            this.minHeight = Math.max(min, this.minimumHeight);
          }
        }
      }
    } else {
      this.minHeight = min;
    }

    this.minHeight = Math.floor(this.minHeight);
  }

  calculateMinWidth() {
    let min = Number.POSITIVE_INFINITY;

    for (let img of this.imgs.toArray()) {
      if (!!img.nativeElement.offsetWidth) {
        min = Math.min(min, img.nativeElement.offsetWidth);
      }
    }

    if (min == Number.POSITIVE_INFINITY) {
      min = 0;
    }

    if (!!this.minimumWidth || !!this.maximumWidth) {
      if (!!this.minimumWidth && !!this.maximumWidth) {
        this.minWidth = Math.max(this.minimumWidth, Math.min(min, this.maximumWidth));
      } else {
        if (!!this.maximumWidth) {
          this.minWidth = Math.min(min, this.maximumWidth);
        }
        if (!!this.minimumWidth) {
          this.minWidth = Math.max(min, this.minimumWidth);
        }
      }
    } else {
      this.minWidth = min;
    }

    this.minWidth = Math.floor(this.minWidth);
  }

  nextImage() {
    this.toggleState();

    if (this.imageIndex < this.images.length - 1) {
      this.imageIndex++;
    } else {
      this.imageIndex = 0;
    }

    this.onImageChangeStart.emit(this.imageIndex);

    this.toggleState();

    this.onImageChangeStop.emit();
  }

  previousImage() {
    this.toggleState();

    if (this.imageIndex > 0) {
      this.imageIndex--;
    } else {
      this.imageIndex = this.images.length - 1;
    }

    this.onImageChangeStart.emit(this.imageIndex);

    this.toggleState();

    this.onImageChangeStop.emit();
  }

  viewImage(index: number) {
    if (index <= this.images.length - 1) {
      this.toggleState();

      this.imageIndex = index;

      this.onImageChangeStart.emit(this.imageIndex);

      this.toggleState();

      this.onImageChangeStop.emit();
    }
  }

  onImageLoad(index?: number, error?: boolean) {
    this.loadedImages++;

    if (error) {
      this.imagesNotLoaded.push(index);
    }

    if (this.loadedImages >= (this.images == null ? 0 : this.images.length)) {

      if (this.imagesNotLoaded.length == 0) {

        if (this.equalDimension) {
          if (!this.fullHeight) {
            this.calculateMinHeight();
          } else {
            this.calculateMinWidth();
          }
        }

        this.statuses = [];

        for (let image of this.images) {
          this.statuses.push('transparent');
        }

        this.toggleState();

        if (this.images != null && this.images.length > 1 && this.autoSlide) {
          this.interval = setInterval(this.nextImage.bind(this), this.imageChangeRate);
        }

        this.initialized = true;
        this.imagesLoaded.emit();
      } else {
        let images = Object.assign([], this.images);

        for (let i of this.imagesNotLoaded) {
          images[i] = this.DEFAULT_IMAGE;
        }

        //DA CONTROLLARE
        //this.loadedImages = 0;
        this.loadedImages -= this.imagesNotLoaded.length;
        this.imagesNotLoaded = [];
        this.images = images;
      }
    }
  }

  toggleState() {
    this.statuses[this.imageIndex] = this.statuses[this.imageIndex] == 'opaque' ? 'transparent' : 'opaque';

    if (!(this.cdRef as ViewRef).destroyed) {
      this.cdRef.detectChanges();
    }
  }

  stopInterval(force: boolean = false) {
    if (force || (this.interval != null && this.showButtons && this.autoSlide)) {
      clearInterval(this.interval);
    }
  }

  resumeInterval(force: boolean = false) {
    if (force || (this.interval != null && this.showButtons && this.autoSlide)) {
      this.interval = setInterval(this.nextImage.bind(this), this.imageChangeRate);
    }
  }

  openGallery() {
    let dialogOptions = new DialogOptions();
    dialogOptions.width = '100vw';
    dialogOptions.height = '100vh';
    dialogOptions.maxHeight = '100vh';
    dialogOptions.panelClass = 'no-padding-dialog';

    let ref = this.showModalDialog(ImageSliderGalleryComponent, dialogOptions);
    let component = <ImageSliderGalleryComponent>ref.componentInstance;
    component.images = this.images;
    component.descriptions = this.descriptions;
    component.imageIndex = this.imageIndex;
  }

  ngOnDestroy() {
    this.sub.unsubscribe();

    if (this.sub1 != null) {
      this.sub1.unsubscribe();
    }

    if (this.sub2 != null) {
      this.sub2.unsubscribe();
    }

    if (this.sub3 != null) {
      this.sub3.unsubscribe();
    }

    if (this.sub4 != null) {
      this.sub4.unsubscribe();
    }

    clearInterval(this.interval);
  }
}